/**
 * Direction A tokens for Cluvik Admin — the one place identity artwork is sized
 * and coloured.
 *
 * Authority: DS_DIRECTION_A_APPROVED.md in sk-amroar/cluvik-platform, approved
 * 25 August 2026. That file is the durable specification for BOTH applications;
 * the values here are transcribed from it, not chosen here. If the two ever
 * disagree, the Platform file wins and this one is wrong.
 *
 * WHY ADMIN HAS ITS OWN COPY RATHER THAN AN IMPORT. The two applications are
 * separate repositories with separate builds and no shared package. The honest
 * options were a duplicated constant or a fabricated dependency; a duplicate
 * that names its source and is checked against it is the smaller cost. The test
 * beside this file asserts the ramp values character for character, so a drift
 * is a failure rather than a slow divergence nobody sees.
 *
 * THE RULE THIS EXISTS TO KEEP, restated because it is the one most easily
 * lost in a control plane full of tables:
 *
 *     DIMENSIONAL ARTWORK = IDENTITY.   FLAT MONOCHROME = ACTION.
 *
 * A filter control, an edit pencil, a delete button and a table sort arrow are
 * actions. None of them ever become 3D artwork. Turning an action into identity
 * artwork is not a richer version of the same thing — it tells an operator that
 * the control is a place, and places do not do anything when you click them.
 */

/**
 * The Cluvik ramp: violet #6030F0 -> blue #2050E0 -> cyan #00C8D8, eight
 * positions, each [light, mid, dark] for the lit face, body and extruded side.
 *
 * The palette IS the mark. Every value is a position sampled from the logo, not
 * a hue picked to make two icons differ. If two identities are hard to tell
 * apart the fix is geometry, never a new colour.
 */
export const RAMP = {
  violet: ['#9B7BFF', '#6030F0', '#3A0FA8'],
  indigo: ['#8A85FF', '#4F46E5', '#2A2299'],
  iris: ['#7A8CFF', '#3B54E8', '#1C2C9E'],
  blue: ['#6BA3FF', '#2050E0', '#0F2F9C'],
  azure: ['#5FBCF5', '#1B8FE0', '#0B4E8C'],
  sky: ['#5FD2F0', '#0FA8D8', '#075C7A'],
  cyan: ['#63E6EE', '#00C8D8', '#00707E'],
  aqua: ['#6BEFDF', '#00BCB4', '#006A66'],
};

export const RAMP_KEYS = Object.keys(RAMP);

/**
 * The one secondary: attention, value, trajectory. Never differentiation, and
 * never a ninth identity colour.
 */
export const ACCENT = '#FFC24D';

/**
 * Destructive intent. A CONTROL state, not an identity colour — it belongs on a
 * button whose activation destroys something and nowhere else, least of all on
 * a resting toolbar or a status chip for a merely unhealthy connection.
 */
export const DESTRUCTIVE = '#DC2626';
export const DESTRUCTIVE_DARK = '#F87171';

/** The full artwork square, not an inner glyph box. Approved scale. */
export const IDENTITY_SIZES = {
  xs: 18,    // inline reference · breadcrumb
  sm: 22,    // list row · table cell
  md: 28,    // dropdown option · nav item
  lg: 40,    // page header · panel header
  xl: 52,    // area card · module tile
  '2xl': 76, // empty state · onboarding · provisioning moment
};

export const IDENTITY_SIZE_KEYS = Object.keys(IDENTITY_SIZES);

/** Identity artwork is never rendered below identity-xs. */
export const MIN_IDENTITY_PX = IDENTITY_SIZES.xs;

/**
 * Depth scales with size, and that is optical rather than a performance budget:
 * a cast shadow and an ambient-occlusion pool at 18px are mud, not depth. They
 * are omitted below these thresholds because they make the mark WORSE there.
 */
export const DEPTH_THRESHOLDS = {
  ribbon: 22,
  occlusion: 22,
  castShadow: 28,
};

export function identityPx(size) {
  if (typeof size === 'number') return Math.max(size, MIN_IDENTITY_PX);
  return IDENTITY_SIZES[size] || IDENTITY_SIZES.md;
}

/** Which depth effects a given rendered size earns. */
export function depthFor(px) {
  return {
    ribbon: px >= DEPTH_THRESHOLDS.ribbon,
    occlusion: px >= DEPTH_THRESHOLDS.occlusion,
    castShadow: px >= DEPTH_THRESHOLDS.castShadow,
  };
}

const identityTokens = {
  RAMP, RAMP_KEYS, ACCENT, DESTRUCTIVE, DESTRUCTIVE_DARK,
  IDENTITY_SIZES, IDENTITY_SIZE_KEYS, MIN_IDENTITY_PX,
  DEPTH_THRESHOLDS, identityPx, depthFor,
};

export default identityTokens;
